import { useState } from 'react';
import { Alert, Box, Button, Card, Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import ApprovalIcon from '@mui/icons-material/Check';
import EditOutlined from '@mui/icons-material/EditOutlined';
import RejectIcon from '@mui/icons-material/DeleteOutlined';
import { DataTable } from '../../components/DataTable';
import { useAction } from '../../lib/useAction';
import { StatusChip } from '../../components/StatusChip';
import { money } from '../../lib/money';
import { fileSize } from '../../lib/text';
import { useCustomers } from '../customers/useCustomers';
import { CardChat, type Talk } from './CardChat';
import { EmailEditor } from './EmailEditor';
import { KindIcon } from './KindIcon';
import type { EmailFields } from './useApprovals';
import { STATUS_LABEL, type Approval, type ApprovalStatus, type Decision } from './sample';

const STATUS_TONE = {
  pending: 'warning',
  approved: 'info',
  sent: 'success',
  rejected: 'neutral',
  failed: 'error',
} as const;

/** What Claude would send, written out: the letter for a quotation, the message, or the cost lines of a JCS */
function Proposal({ a }: { a: Approval }) {
  const customers = useCustomers().items;
  if (a.lines) {
    const total = a.lines.reduce((n, [, v]) => n + v, 0);
    const rows = [...a.lines.map(([d, v]) => ({ d, v, bold: false })), { d: 'Total', v: total, bold: true }];
    return (
      <DataTable<(typeof rows)[number]>
        rows={rows}
        rowKey={(r) => r.d}
        columns={[
          { header: 'Description', primary: true, render: (r) => (r.bold ? <b>{r.d}</b> : r.d) },
          {
            header: 'Amount',
            nowrap: true,
            render: (r) => (r.bold ? <b>{r.v.toLocaleString('en-US')}</b> : r.v.toLocaleString('en-US')),
          },
          { header: 'Currency', nowrap: true, render: () => a.linesCurrency },
        ]}
      />
    );
  }
  if (a.quote) {
    const who = a.subtitle.split(' · ')[0] ?? '';
    const first = (customers.find((c) => c.name === who)?.contact ?? '').split(' ')[0];
    const transit = a.quote.keyPoints.find((k) => k.startsWith('Transit time'))?.replace('Transit time: ', '');
    const service = a.quote.shipment.find(([k]) => k === 'Transport Mode')?.[1];
    const route = a.subtitle.split(' · ')[1] ?? '';
    return (
      <Stack spacing={1} sx={{ fontSize: 14 }}>
        <span>Dear {first || 'customer'},</span>
        <span>Please find our quotation for the shipment {route} as per your request.</span>
        <Box>
          <b>Total: {money(a.quote.suggested, a.quote.currency)}</b>
          <br />
          {transit && <b>Transit time: {transit}</b>}
          {transit && <br />}
          {service && <b>Service: {service}</b>}
        </Box>
        <span>Best regards, SERB Express</span>
      </Stack>
    );
  }
  return (
    <Stack spacing={1}>
      {a.to && <EmailDetails a={a} />}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        {a.body}
      </Typography>
    </Stack>
  );
}

/** Who gets the email, its subject and the files that go with it: what you check before approving */
function EmailDetails({ a }: { a: Approval }) {
  const row = (label: string, value: string) => (
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      <b>{label}:</b> {value}
    </Typography>
  );
  return (
    <Stack spacing={0.25} sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
      {row('To', a.to ?? '')}
      {a.cc && row('CC', a.cc)}
      {row('Subject', a.subject ?? '')}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <b>Attachments:</b>
        {a.files?.length ? (
          a.files.map((f) => (
            <Chip
              key={f.path}
              size="small"
              label={`${f.name} · ${fileSize(f.size)}`}
              color={f.size === null ? 'error' : 'default'}
            />
          ))
        ) : (
          <span>none</span>
        )}
      </Stack>
    </Stack>
  );
}

const NOTE: Record<
  Exclude<ApprovalStatus, 'pending'>,
  { severity: 'warning' | 'success' | 'info' | 'error'; text: string }
> = {
  approved: { severity: 'warning', text: 'You approved this. It is being sent.' },
  sent: { severity: 'success', text: 'Sent.' },
  rejected: { severity: 'info', text: 'You rejected this. Nothing was sent.' },
  failed: {
    severity: 'error',
    text: 'You approved this, but sending failed. Tell Claude what to fix, or send it yourself.',
  },
};

type Props = {
  a: Approval;
  onDecide: (id: string, status: Decision, note: string) => Promise<void>;
  onEdit: (id: string, final: EmailFields, note: string) => Promise<void>;
  onInstruct: (id: string, text: string, kind: Talk) => Promise<void>;
  /** on a phone the card opens over the list; this goes back to it */
  onBack?: () => void;
};

/** One proposal in the reading pane: what it is, what Claude wrote, your chat with Claude, and Approve / Reject. An email goes out from the server only after you approve it. */
export function ProposalCard({ a, onDecide, onEdit, onInstruct, onBack }: Props) {
  const [editing, setEditing] = useState(false);
  const [said, setSaid] = useState('');
  const urgent = a.status === 'pending' && a.urgency === 'High';
  const [who, route] = a.subtitle.split(' · ');

  const approve = useAction(() => onDecide(a.id, 'approved', 'You approved this.'), {
    success: a.to ? 'Approved. The server is sending it now.' : 'Approved. Claude is carrying it out now.',
  });
  // what you typed in the chat box is the reason Claude reads with the rejection
  const reject = useAction(() => onDecide(a.id, 'rejected', said.trim() || 'You rejected this'), {
    success: 'Rejected. Nothing was sent.',
  });

  return (
    <Card id={`approval-${a.id}`} sx={{ scrollMarginTop: 80 }}>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {onBack && (
            <IconButton aria-label="Back to the list" onClick={onBack} edge="start">
              <BackIcon />
            </IconButton>
          )}
          <KindIcon kind={a.kind} size={48} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: 20 }}>{a.srb}</Typography>
              <StatusChip
                label={a.status === 'pending' ? 'Pending Approval' : STATUS_LABEL[a.status]}
                tone={STATUS_TONE[a.status]}
              />
              {urgent && <StatusChip label="Urgent" tone="error" />}
            </Stack>
            <Typography sx={{ fontWeight: 700 }}>{a.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[a.to ? null : who, route, a.created].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Stack>
        {a.status === 'pending' && (a.inSpark || a.editedInSpark) && (
          <Typography variant="body2" color="text.secondary">
            {a.editedInSpark ? 'Edited by you in Spark' : 'Draft ready in Spark'}
          </Typography>
        )}
        {a.to && (
          <Typography variant="body2" sx={{ fontWeight: 700, color: a.replyIn ? 'text.secondary' : 'warning.main' }}>
            {a.replyIn ? `Reply-all in: ${a.replyIn}` : 'NEW EMAIL'}
          </Typography>
        )}

        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {a.status === 'pending' ? "Claude's Proposal" : a.to ? 'The Email' : "Claude's Proposal"}
          </Typography>
          {editing ? (
            <EmailEditor
              a={a}
              onCancel={() => setEditing(false)}
              onApprove={(fields) => onEdit(a.id, fields, 'You approved your own version.')}
            />
          ) : (
            <Proposal a={a} />
          )}
        </Box>

        {a.status !== 'pending' && (
          <Alert severity={NOTE[a.status].severity}>
            {a.status === 'approved' && !a.to ? 'You approved this. Claude is carrying it out.' : NOTE[a.status].text}
          </Alert>
        )}

        <Divider />
        <CardChat a={a} text={said} onText={setSaid} onSend={onInstruct} />
      </Stack>

      {a.status === 'pending' && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 1.5,
            gap: 1,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            borderRadius: '0 0 inherit inherit',
          }}
        >
          <Button
            color="error"
            variant="outlined"
            startIcon={<RejectIcon />}
            disabled={reject.busy}
            onClick={() => reject.run(undefined)}
          >
            Reject
          </Button>
          {a.to && !editing && (
            <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit Email
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<ApprovalIcon />}
            disabled={approve.busy}
            onClick={() => approve.run(undefined)}
          >
            Approve
          </Button>
        </Stack>
      )}
    </Card>
  );
}
